import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Paper,
  Grid,
  Box,
} from "@mui/material";
import { styles } from "../../styles";
import { DataModalPuAmCMGenie } from "./ModalDataPuAmCMGenie";
import { MButton, MCheckbox, ResponsiveFormContainer } from "@app/components";
import { useFormContext } from "react-hook-form";
import { ResultDataTable } from "../commonForms/calculation";
import PDFGeneratorService from "@app/pages/samples/utils/PDFGenerator";
import { useParams } from "react-router-dom";
import { useSample } from "@app/pages/samples/utils/useSample";
import { toExponentialUppercaseWhitZero } from "@app/utils/functions";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";

interface ResultsTableProps {
  totalActivity?: number;
  totalUncertainty?: number;
  totalLid?: number;
  efficiencieTotal?: number;
  efficiencieMedia?: number;
  fetchTrigger?: boolean;
}

const ResultsTablePuAmCMGenie: React.FC<ResultsTableProps> = ({
  totalActivity,
  totalUncertainty,
  totalLid,
  efficiencieTotal,
  efficiencieMedia,
  fetchTrigger,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const form = useFormContext();
  const { idMuestra } = useParams();
  const sample = useSample.getState().get(Number(idMuestra));

  const { fetchCalculation, loadingResults, putResult } = useAnalysis();
  const [results, setResults] = useState<ResultDataTable[]>([]);
  const [resultsAll, setResultsAll] = useState<any>({});

  useEffect(() => {

    if (totalActivity !== undefined && totalUncertainty !== undefined) {
      setResults([
        { label: "ACTIVIDAD", value: Number(totalActivity) },
        { label: "INCERTIDUMBRE", value: Number(totalUncertainty) },
        { label: "LID", value: Number(totalLid) },
      ]);
    }
  }, [totalActivity, totalUncertainty, totalLid, efficiencieTotal, efficiencieMedia]);
  //}, [totalActivity, totalUncertainty, totalLid]);

  const formatResults = useCallback((response: any): ResultDataTable[] => {   
    return [
      { label: "ACTIVIDAD", value: Number(totalActivity || response.activity) },
      { label: "INCERTIDUMBRE", value: Number(totalUncertainty || response.uncertainty) },
      { label: "LID", value: Number(totalLid || response.lid) },
    ];
  }, [totalActivity, totalUncertainty, totalLid, efficiencieTotal, efficiencieMedia]);

  useEffect(() => {
    const preloadResults = async () => {
      const idResultPreload = form.getValues("idResultPreload");
      if (idResultPreload !== undefined) {
        const response = await fetchCalculation(idResultPreload);
        if (response) {
          form.setValue("to_report", response.to_reports ? "to_report" : "");
          
          const efficiency_sr_89 = response.efficiency_sr_89;
          const inc_rel_squared_performance = response.inc_rel_squared_performance;
          response.efficiency_sr_89 = inc_rel_squared_performance;
          response.inc_rel_squared_performance = efficiency_sr_89;
          setResultsAll(response);
          setResults(formatResults(response));
        }
      } else {
        // Si no hay resultado guardado, construir objeto temporal con valores actuales del formulario
        const allFormValues = form.getValues();
        
        const detectorPerformance = efficiencieMedia && (efficiencieTotal || allFormValues.efficiencieTotal) 
          ? ((efficiencieTotal || allFormValues.efficiencieTotal) / efficiencieMedia) * 100 
          : 0;
        
        const tempResultsAll = {
          activity: totalActivity || allFormValues.totalActivity,
          uncertainty: totalUncertainty || allFormValues.totalUncertainty,
          lid: totalLid || allFormValues.totalLid,
          efficiency: efficiencieTotal || allFormValues.efficiencieTotal,
          efficiency_sr_89: efficiencieMedia,
          inc_rel_squared_performance: detectorPerformance,
          // Agregar datos adicionales del formulario
          calculation_date_countig_file: allFormValues.calculation_date_countig_file,
          calculation_equipment_file_generic: allFormValues.calculation_equipment_file_generic,
          calculation_detectors_generic: allFormValues.calculation_detectors_generic,
          calculation_time_count: allFormValues.calculation_time_count,
          calculation_volume: allFormValues.calculation_volume,
          calibration_source_certificate: allFormValues.calibration_source_certificate,
          calculation_background: allFormValues.calculation_background,
          calculation_calibration: allFormValues.calculation_calibration,
          calculation_library: allFormValues.calculation_library,
          calculation_sample: allFormValues.calculation_sample,
        };
        
        setResultsAll(tempResultsAll);
      }
    };

    preloadResults();
  }, [form.getValues("idResultPreload"), fetchTrigger, totalActivity, totalUncertainty, totalLid, efficiencieTotal, efficiencieMedia]);

  const handleOpenPuAmCMGenie = () => {
    setOpen(true);
  };

  const handleToReportUpdate = () => {
    if (form.getValues("idResultPreload")) {
      putResult(form.getValues("idResultPreload"), true);
      form.setValue("to_report", "to_report");
    }
  };

  const setTypeAnalisisMemo = useMemo(() => {
    const typeAnalysis = form.getValues("typeAnalysis");

    return typeAnalysis;
  }, [form.getValues("typeAnalysis")]);

  return (
    <>
      {form.getValues("idResultPreload") &&
        !loadingResults &&
        results?.length > 0 && (
          <>
            <ResponsiveFormContainer columns={1} ml={0.5}>
              <Box display="flex" justifyContent="center" mt={2}>
                <MCheckbox
                  key={JSON.stringify(form.watch("to_report"))}
                  name="to_report"
                  options={[
                    {
                      label: "Para informe",
                      value: "to_report",
                    },
                  ]}
                  multiple={false}
                  disabled={true}
                />
                <MButton
                  variant="contained"
                  color="primary"
                  text="Utilizar resultado en informe"
                  onClick={handleToReportUpdate}
                  disabled={form.getValues("to_report") === "to_report"}
                />
              </Box>
            </ResponsiveFormContainer>
            <Grid
              container
              spacing={2}
              p={4}
              justifyContent="center"
              alignItems="end"
            >
              <Grid item xs={12} md={12} className="flexLeft">
                <Typography variant="h6" sx={styles.resultsTableHeader}>
                  RESULTADOS:
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}></Grid>
              <Grid item xs={12} md={6} className="flexCenter">
                <TableContainer component={Paper}>
                  <Table sx={styles.resultsTable}>
                    <TableBody>
                      {results.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              ...styles.resultsTableCell,
                              ...styles.resultsTableCellLabel,
                            }}
                          >
                            {result.label}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...styles.resultsTableCell,
                              ...styles.resultsTableCellValue,
                            }}
                          >
                            {toExponentialUppercaseWhitZero(
                              Number(result.value),
                              2
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={3}></Grid>

              <Grid
                item
                xs={12}
                md={12}
                display="flex"
                justifyContent="flex-end"
                gap={2}
              >
                <MButton
                  variant="outlined"
                  color="secondary"
                  text="Visualizar"
                  onClick={() =>
                    PDFGeneratorService.downloadPDFDocument(
                      { ...form.getValues(), ...sample, ...resultsAll },
                      results,
                      resultsAll
                    )
                  }
                />
                <MButton
                  onClick={handleOpenPuAmCMGenie}
                  text=" Datos de salida"
                  color="primary"
                />
              </Grid>
            </Grid>

            <DataModalPuAmCMGenie
              open={open}
              handleClose={() => setOpen(false)}
              data={resultsAll}
              typeAnalysis={setTypeAnalisisMemo}
              efficiencieTotal={efficiencieTotal}
              efficiencieMedia={efficiencieMedia}
            />
          </>
        )}
    </>
  );
};

export default ResultsTablePuAmCMGenie;